import { Decorative } from "@faire/design-tokens";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Global } from "@faire/web/slate/icons/foundation/Global";
import { Shipping } from "@faire/web/slate/icons/foundation/Shipping";
import { ShipsFrom } from "@faire/web/slate/icons/foundation/ShipsFrom";
import { Column, Flex } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import { Typography, TypographyVariants } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useRef } from "react";
import Slider from "react-slick";
import styled from "styled-components";

import { getImageSlider } from "@faire/retailer/features/EuropeanBrands/ShopEuropeBanner/Images";
import { getIsApparelRetailer } from "@faire/retailer/serialized-data/getIsApparelRetailer";

export const ShopEuropeBanner: React.FC = () => {
  const sliderRef = useRef<Slider | null>(null);
  const isApparelRetailer = getIsApparelRetailer() ?? false;
  const isSSR = useIsSSR();

  return (
    <Wrapper>
      {getImageSlider(isApparelRetailer, sliderRef, isSSR)}
      <Content>
        <StyledColumnCategoryPage>
          <BannerHeaderMessage isApparelRetailer={isApparelRetailer} />
          <Spacer height="4x" />
          <BannerBulletMessage isApparelRetailer={isApparelRetailer} />
        </StyledColumnCategoryPage>
      </Content>
    </Wrapper>
  );
};

const BannerHeaderMessage: React.FC<{ isApparelRetailer: boolean }> = ({
  isApparelRetailer,
}) => {
  const titleTypographyVariant: ResponsiveValues<TypographyVariants> = {
    xLargeAndAbove: "displaySSerifRegular",
    tabletAndAbove: "pageHeaderSerifRegular",
    mobileAndAbove: "displaySSerifRegular",
  };

  if (isApparelRetailer) {
    return (
      <>
        <Typography variant={titleTypographyVariant}>
          <StrictLocalMsg
            defaultMessage="The best European styles, "
            description={{
              text: "European brands banner text",
              img: "https://cdn.faire.com/fastly/ec48def49735a4ab9edee0a1ca2eee0a1243f5c9f53d95c97011c0beb4d77c61.png",
            }}
          />
        </Typography>
        <Typography variant={titleTypographyVariant}>
          <StrictLocalMsg
            defaultMessage="delivered to your store."
            description={{
              text: "European brands banner text",
              img: "https://cdn.faire.com/fastly/ec48def49735a4ab9edee0a1ca2eee0a1243f5c9f53d95c97011c0beb4d77c61.png",
            }}
          />
        </Typography>
      </>
    );
  }

  return (
    <>
      <Typography variant={titleTypographyVariant}>
        <StrictLocalMsg
          defaultMessage="The best European brands, "
          description={{
            text: "European brands banner text",
            img: "https://cdn.faire.com/fastly/eb5a773dca0173a63d4de4f35daee2296058e00ad7c8d38f7349b8ad54a1999c.png",
          }}
        />
      </Typography>
      <Typography variant={titleTypographyVariant}>
        <StrictLocalMsg
          defaultMessage="delivered fast."
          description={{
            text: "European brands banner text",
            img: "https://cdn.faire.com/fastly/eb5a773dca0173a63d4de4f35daee2296058e00ad7c8d38f7349b8ad54a1999c.png",
          }}
        />
      </Typography>
    </>
  );
};

const BannerBulletMessage: React.FC<{ isApparelRetailer: boolean }> = ({
  isApparelRetailer,
}) => {
  if (isApparelRetailer) {
    return (
      <>
        <Typography variant="paragraphSansRegular">
          <GlobalIcon />
          <StrictLocalMsg
            defaultMessage="High-quality brands from Europe"
            description={{
              text: "Bullet item text for apparel EU brands",
              img: "https://cdn.faire.com/fastly/ec48def49735a4ab9edee0a1ca2eee0a1243f5c9f53d95c97011c0beb4d77c61.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <Typography variant="paragraphSansRegular">
          <ShipsFromIcon />
          <StrictLocalMsg
            defaultMessage="Free returns on all opening orders"
            description={{
              text: "Bullet item text for free returns",
              img: "https://cdn.faire.com/fastly/ec48def49735a4ab9edee0a1ca2eee0a1243f5c9f53d95c97011c0beb4d77c61.png",
            }}
          />
        </Typography>
      </>
    );
  }

  return (
    <>
      <Typography variant="paragraphSansRegular">
        <GlobalIcon />
        <StrictLocalMsg
          defaultMessage="High-quality goods from Europe"
          description={{
            text: "Bullet item text for non-apparel EU goods",
            img: "https://cdn.faire.com/fastly/eb5a773dca0173a63d4de4f35daee2296058e00ad7c8d38f7349b8ad54a1999c.png",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography variant="paragraphSansRegular">
        <ShippingIcon />
        <StrictLocalMsg
          defaultMessage="Average 7-day delivery time"
          description={{
            text: "Bullet item text for delivery time",
            img: "https://cdn.faire.com/fastly/eb5a773dca0173a63d4de4f35daee2296058e00ad7c8d38f7349b8ad54a1999c.png",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography variant="paragraphSansRegular">
        <ShipsFromIcon />
        <StrictLocalMsg
          defaultMessage="Free returns on all opening orders"
          description={{
            text: "Bullet item text for free returns",
            img: "https://cdn.faire.com/fastly/eb5a773dca0173a63d4de4f35daee2296058e00ad7c8d38f7349b8ad54a1999c.png",
          }}
        />
      </Typography>
    </>
  );
};

const Wrapper = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", tabletAndAbove: "row-reverse" },
})``;

const Content = styled(Column)`
  background-color: ${Decorative.tint.neutral};
  width: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    height: 295px;
  }
`;

const StyledColumnCategoryPage = styled(Column)`
  padding: ${getSpacing("5x", "7x")};

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("4x", "2x")};
  }
`;

const ShipsFromIcon = styled(ShipsFrom)`
  position: relative;
  top: 3px;
  margin-right: 10px;
`;

const ShippingIcon = styled(Shipping)`
  position: relative;
  top: 3px;
  margin-right: 10px;
`;

const GlobalIcon = styled(Global)`
  position: relative;
  top: 3px;
  margin-right: 10px;
`;
